import { message } from 'antd';
import { useAssegnaParereMassivo } from 'client/api/backend/attivita/attivita';
import {
  AssegnaParereMassivoTipo,
  AttivitaDTO,
  BEResponseDatiSpecificiRisposta
} from 'client/api/backend/schemas';
import { logger } from 'client/core/logger/logger';
import { getResponseErrorMessage } from 'client/core/network/errors/getResponseErrorMessage';
import { FormikAugmented } from 'client/ui/form/FormikAugmented';
import { ModalDialog } from 'client/ui/modal/ModalDialog';
import { yup } from 'common/validation/initYup';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import {
  ActionTypeTasksMassiveForm,
  LOADING_ACTION_TYPE_MESSAGE_KEY
} from './ActionTypeTasksMassiveForm';

export interface ITaskInError {
  esito: 'error';
  taskId: string;
  message: string;
}
export interface ITaskSuccess {
  esito: 'success';
  taskId: string;
}

export type ITaskResult = ITaskInError | ITaskSuccess;

export interface IActionTypeTasksMassiveModalProps {
  tasks: AttivitaDTO[];
  visible: boolean;
  hide: () => void;
  type: AssegnaParereMassivoTipo;
  modalTitle: string;
  actionText: string;
}

/**
 * Modale per l'assegnazione massiva di un parere
 */
export function ActionTypeTasksMassiveModal(
  props: IActionTypeTasksMassiveModalProps
) {
  const { tasks, visible, type, modalTitle, actionText } = props;
  const actionFunction = useAssegnaParereMassivo();
  const [progress, setProgress] = useState(0);
  const [taskResults, setTaskResults] = useState<ITaskResult[]>([]);
  const isRunning = useRef(true);
  const queryClient = useQueryClient();

  /** Metodo che esegue una singola attività e ritorna il risultato */
  const runActionOnTask = useCallback(
    async (
      task: AttivitaDTO,
      values: IActionTypeMassiveTasksValues
    ): Promise<ITaskResult[]> => {
      try {
        await actionFunction.mutateAsync({
          idTaskInstance: task.idTaskInstance!,
          params: { tipo: type }
        });
        setProgress(progress => progress + 1);
        return [
          {
            esito: 'success',
            taskId: task.procedimento!.numero!
          }
        ];
      } catch (e: any) {
        logger.error({ error: e });

        // Recupero la response dall'errore
        const response = e?.response?.data as BEResponseDatiSpecificiRisposta;

        // Raccolgo tutti gli errori riscontrati che si trovano dentro l'esito delle singole dichiarazioni
        const errors = (response.data?.dichiaraziones
          ?.map(d => d.esito?.messaggioDiErrore)
          .filter(message => !!message) ?? []) as string[];

        // Se ci sono errori specifici, ritorno un array di errori
        if (errors.length > 0) {
          return errors.map(message => ({
            esito: 'error',
            taskId: task.procedimento!.numero!,
            message
          }));
        }

        // Se non ci sono errori specifici, allora ritorno il messaggio globale della chiamata API
        return [
          {
            esito: 'error',
            taskId: task.procedimento!.numero!,
            message: getResponseErrorMessage(
              e,
              "Errore durante l'esecuzione dell'azione massiva"
            )!
          }
        ];
      }
    },
    [actionFunction]
  );

  /** Reset della progress bar e dei risultati */
  const reset = () => {
    setProgress(0);
    setTaskResults([]);
    isRunning.current = !!visible;
  };

  /** Informo l'utente che l'operazione è stata annullata  */
  const handleStop = () => {
    props.hide();
    message.destroy(LOADING_ACTION_TYPE_MESSAGE_KEY);
    message.warning('Le attività sono state modificate parzialmente');
    // Invalida le query per aggiornare la tabella
    queryClient.invalidateQueries();
  };

  useEffect(() => {
    reset();
  }, [visible]);

  /** Metodo che itera su tutte le attività e le completa */
  const runTasks = useCallback(
    async (values: IActionTypeMassiveTasksValues) => {
      const currentTaskResults: ITaskResult[] = [];
      for (const task of tasks) {
        // Se l'utente ha annullato l'operazione, interrompe il ciclo e mostra un messaggio di warning
        if (!isRunning.current) {
          handleStop();
          return;
        }

        const taskResult = await runActionOnTask(task, values);
        currentTaskResults.push(...taskResult);
      }

      setTaskResults(currentTaskResults);

      // Se non ci sono errori, mostra un messaggio di successo e chiude il Modal
      if (!currentTaskResults.some(result => result.esito === 'error')) {
        message.success('Attività modificate con successo');
        props.hide();
      }
    },
    [tasks]
  );

  return (
    <ModalDialog
      title={modalTitle}
      visible={visible}
      size="medium"
      footer={null}
      closable={false}
    >
      <FormikAugmented
        enableReinitialize
        validationSchema={actionTypeMassiveTasksValidator}
        validationContext={{}}
        initialValues={actionTypeMassiveTasksValidator.cast({})}
        onSubmit={async (rawValues: IActionTypeMassiveTasksValues, helpers) => {
          const values = await actionTypeMassiveTasksValidator.validate(
            rawValues
          );

          try {
            await runTasks(values);
          } catch (e) {
            logger.error({ error: e });
            message.error(`Si è verificato un errore durante l'esecuzione dell'azione massiva. Riprovare...`); // prettier-ignore
          } finally {
            queryClient.invalidateQueries();
          }
        }}
      >
        <ActionTypeTasksMassiveForm
          tasks={tasks}
          taskResults={taskResults}
          progress={progress}
          hide={props.hide}
          isRunning={isRunning}
          actionText={actionText}
        />
      </FormikAugmented>
    </ModalDialog>
  );
}

const actionTypeMassiveTasksValidator = yup.object({}).required().noUnknown();

type IActionTypeMassiveTasksValues = yup.InferType<
  typeof actionTypeMassiveTasksValidator
>;
